import { getItemDef } from "../items/db.js";
import {
  fittedMagazine,
  fittedMods,
  findModHost,
  isTrueMagazine,
  weaponWithMagRounds,
  weaponWithModAt,
} from "../items/mods.js";
import {
  MAX_MOD_DEPTH,
  WEAPON_EQUIP_SLOTS,
  canEquipInSlot,
  isStackable,
  type AmmoItemDef,
  type AttachKind,
  type AttachmentItemDef,
  type ContainerItemDef,
  type EquipSlot,
  type ItemDef,
  type ItemInstance,
  type MagazineItemDef,
  type WeaponEquipSlot,
  type WeaponItemDef,
  type WeaponSlotDef,
} from "../items/types.js";
import {
  cloneGrid,
  createContainerGrid,
  findFreePlacement,
  placementProblem,
  type GridState,
  type Rotation,
} from "./grid.js";
import {
  containerAccepts,
  contentsGridFor,
  findNestedItem,
  subtreeDepth,
  MAX_GRID_DEPTH,
} from "./nesting.js";
import { bodySlotsGrid, equipmentFromBodySlots, type EquipmentRecord } from "./body.js";
import {
  cloneInventory,
  findEquipped,
  type GridAreaId,
  type Inventory,
} from "./state.js";

/**
 * THE single mutation path for inventories: every drag, equip and split, on
 * the client's optimistic copy and the server's authoritative one, goes
 * through `applyInventoryOp`. PURE — fresh objects, input untouched, a rejected
 * op leaves the context byte-identical (the fuzz test asserts identity).
 * Instances are shared between old and new context (immutable by convention).
 * The grid's rules live here; the world's (alive, reach, rate, ownership) are
 * the server's, with its own reasons.
 *
 * NESTING. A container carries its contents on `ItemInstance.contents`, so an
 * op can address a top-level grid, a worn rig/backpack's hoisted grid, or a
 * contents grid named `{ area: "inside", iid }`. COPY-ON-WRITE IS NOT
 * OPTIONAL: `cloneCtx` gives fresh grids but shared instances, and a nested
 * grid lives inside an instance — so before any nested grid is touched its
 * whole host chain is freshened inside the Work copy, outermost first, and
 * MEMOIZED in `Work.owned` (a second clone would orphan references the op
 * already holds). `normalize` then deletes `contents` from any host that ended
 * up empty, so an emptied box is byte-identical to a fresh one. Reload, med
 * and ammo helpers do not descend — a round in a closed box is stowed.
 */

/**
 * Where a grid is. `"container"` is whatever is open — one slot, since a
 * player looks into one thing at a time. `"inside"` is not an area: a nested
 * grid is named by its host's iid.
 */
export type AreaId = GridAreaId | "container";

/**
 * A destination: a top-level area, or the contents grid of container `iid`
 * (anywhere reachable, within `MAX_GRID_DEPTH`).
 */
export type TargetRef =
  | { area: AreaId; x: number; y: number; rot: Rotation }
  | { area: "inside"; iid: string; x: number; y: number; rot: Rotation };

export type InventoryOp =
  /** Pick an item up and put it down somewhere legal — including in place. */
  | { t: "move"; iid: string; to: TargetRef }
  /** Wear it. The item must come from a grid and the slot must be free. */
  | { t: "equip"; iid: string; slot: EquipSlot }
  /** Take it off into a grid. A rig/backpack takes its contents with it. */
  | { t: "unequip"; slot: EquipSlot; to: TargetRef }
  /** Move a worn weapon between weapon slots. Swaps if `to` is occupied and
   *  both guns fit each other's slot. */
  | { t: "reslot"; from: WeaponEquipSlot; to: WeaponEquipSlot }
  /** Peel `count` units off a stack into a new instance elsewhere. */
  | { t: "split"; iid: string; count: number; to: TargetRef }
  /** Pour one stack into another. Partial merges are fine; the rest stays. */
  | { t: "merge"; iid: string; ontoIid: string }
  /**
   * Pour rounds out of ammo stack `iid` into `ontoIid` — a weapon (fills its
   * fitted magazine; refuses with none), a loose true magazine, or a fitted one
   * named directly. Nothing is placed, so no cell, rotation or filter.
   */
  | { t: "load"; iid: string; ontoIid: string }
  /**
   * Pour a magazine's rounds back out into the player's grids. `load` backwards,
   * same three kinds of `iid`. Rounds go wherever they fit (top up first, then
   * new stacks). Partial is success; refused ("full") only when not one round fits.
   */
  | { t: "unload"; iid: string }
  /** Remove it entirely. The server turns the returned instance into a world item. */
  | { t: "drop"; iid: string }
  /**
   * Bolt `iid` onto `weaponIid`'s `slot`. Both may be anywhere reachable, boxes
   * and the open container included. `hostIid` names which host inside the gun
   * owns the slot — the gun when absent (the common case), or a fitted part.
   * `weaponIid` stays the id the inventory can find.
   */
  | { t: "attach"; iid: string; weaponIid: string; slot: AttachKind; hostIid?: string }
  /**
   * Take the part out of `slot` into a grid cell, `move`'s rules. Whatever is
   * fitted to the part comes with it; its footprint is its own.
   */
  | { t: "detach"; weaponIid: string; slot: AttachKind; to: TargetRef; hostIid?: string };

export type InventoryOpType = InventoryOp["t"];

/** What an op operates on: the player, plus whatever they have open. */
export interface InvContext {
  inv: Inventory;
  /**
   * Whatever is open. `equipment` only for a body (a corpse's worn slots;
   * `grid` is then its pockets). See `inventory/body.ts`.
   */
  container: { id: string; grid: GridState; equipment?: EquipmentRecord } | null;
  /** True only for the private menu stash. This controls ownership-sensitive
   *  placement behavior; weapon modification only requires reachability. */
  containerOwned?: boolean;
}

/**
 * Machine-readable rejection reasons:
 *   no-item        nothing with that iid is reachable
 *   no-def         unknown def id (version skew), or an `unload`'s cartridge
 *   no-area        the target area does not exist (no rig, nothing open, or an
 *                  unreachable / non-container `"inside"` host)
 *   bounds         placement runs off the grid
 *   overlap        placement collides
 *   slot-occupied  equipping into a filled slot, or a `reslot` whose occupant
 *                  cannot go the other way
 *   slot-empty     unequipping/detaching from an empty slot, or loading a gun
 *                  with no magazine
 *   empty          unloading an empty magazine
 *   same-slot      a `reslot` onto itself
 *   wrong-slot     the item cannot be worn there
 *   not-empty      a slot's capacity grid still holds something (stale-grid
 *                  guard on `equip`)
 *   not-stackable  split/merge with stackMax 1
 *   bad-count      split count outside (0, stack)
 *   def-mismatch   merging different items, or pouring a second cartridge
 *                  kind into a magazine
 *   same-item      merging onto itself
 *   full           destination stack at stackMax, magazine full, or `unload`
 *                  found nowhere for a single round
 *   not-ammo       `load` handed a non-cartridge
 *   not-attachment the fitted item is not an attachment
 *   no-slot        no mounting point of that kind (or not a weapon); for
 *                  load/unload, a target that is neither weapon nor magazine
 *   incompatible   not on the slot's `accepts`, or the wrong caliber
 *   filtered       the host container refuses that kind
 *   too-deep       past MAX_GRID_DEPTH, or (attach) past MAX_MOD_DEPTH
 *   cycle          a container dropped into itself or its own contents
 * The server adds "rate", "reach", "dead", "not-yours".
 */
export type InvOpReason =
  | "no-item"
  | "no-def"
  | "no-area"
  | "bounds"
  | "overlap"
  | "slot-occupied"
  | "slot-empty"
  | "empty"
  | "same-slot"
  | "wrong-slot"
  | "not-empty"
  | "not-stackable"
  | "bad-count"
  | "def-mismatch"
  | "same-item"
  | "full"
  | "not-ammo"
  | "not-attachment"
  | "no-slot"
  | "incompatible"
  | "filtered"
  | "too-deep"
  | "cycle";

export type InventoryOpResult =
  | { ok: true; ctx: InvContext; dropped?: ItemInstance }
  | { ok: false; reason: InvOpReason };

// ---------------------------------------------------------------------------
// Working copy
// ---------------------------------------------------------------------------

/**
 * One host's contents grid as this Work owns it: `grid` (nothing outside
 * references it), `host` (the fresh instance, safe to edit), `depth` (1 for
 * the contents of a top-level item), `chain` (host iids outermost-in — what
 * the cycle check reads).
 */
interface OwnedContents {
  grid: GridState;
  host: ItemInstance;
  hostDef: ContainerItemDef;
  depth: number;
  chain: readonly string[];
}

/** A mutable clone the op is applied to. Published only if the op succeeds. */
interface Work {
  inv: Inventory;
  container: { id: string; grid: GridState } | null;
  /** The open body's worn slots as the pseudo-grid of `inventory/body.ts`, or
   *  `null` when what is open is not a body. Searched, never targeted. */
  body: GridState | null;
  containerOwned: boolean;
  /** Host iid -> the contents grid this Work has taken ownership of. THE memo
   *  that makes copy-on-write idempotent; see the essay at the top. */
  owned: Map<string, OwnedContents>;
}

function cloneCtx(ctx: InvContext): Work {
  return {
    inv: cloneInventory(ctx.inv),
    container:
      ctx.container === null
        ? null
        : { id: ctx.container.id, grid: cloneGrid(ctx.container.grid) },
    body:
      ctx.container?.equipment === undefined ? null : bodySlotsGrid(ctx.container.equipment),
    containerOwned: ctx.containerOwned === true,
    owned: new Map(),
  };
}

function gridOf(w: Work, area: AreaId): GridState | null {
  switch (area) {
    case "pockets":
      return w.inv.pockets;
    case "rig":
      return w.inv.rig;
    case "backpack":
      return w.inv.backpack;
    case "container":
      return w.container === null ? null : w.container.grid;
  }
}

/**
 * Every top-level grid an op may touch, canonical order, open container last,
 * the body's slots after that (reachable by `locate`, no `AreaId` — a source
 * only).
 */
function searchAreas(w: Work): { area: AreaId | "body"; grid: GridState }[] {
  const out: { area: AreaId | "body"; grid: GridState }[] = [];
  for (const area of ["pockets", "rig", "backpack", "container"] as const) {
    const grid = gridOf(w, area);
    if (grid !== null) out.push({ area, grid });
  }
  if (w.body !== null) out.push({ area: "body", grid: w.body });
  return out;
}

/** The roots every deep search starts from, in canonical order. */
function searchRoots(w: Work): GridState[] {
  return searchAreas(w).map((a) => a.grid);
}

/** Index of `iid` among a grid's own items, or -1. Never descends. */
function indexOfItem(grid: GridState, iid: string): number {
  for (let i = 0; i < grid.items.length; i++) {
    const p = grid.items[i] as { item: ItemInstance };
    if (p.item.iid === iid) return i;
  }
  return -1;
}

/**
 * The contents grid of `hostIid`, owned by this Work. THE copy-on-write door:
 * on a miss it walks the chain inward replacing every ancestor with
 * `{ ...instance, contents: <owned clone> }`, memoizing each; on a hit it
 * returns the memo untouched (re-cloning would alias). Freshening replaces
 * array elements in place and never removes one, so held indices survive.
 *
 * @returns `null` when the host is unreachable or not a container ("no-area").
 */
function resolveContents(w: Work, hostIid: string): OwnedContents | null {
  const memo = w.owned.get(hostIid);
  if (memo !== undefined) return memo;

  const hit = findNestedItem(searchRoots(w), hostIid);
  if (hit === null) return null;
  const chain = [...hit.chain, hostIid];

  // `chain[0]` is a top-level item by construction, so exactly one of the
  // search areas holds it — and that grid is already Work-owned.
  const rootIid = chain[0] as string;
  let current: GridState | null = null;
  for (const { grid } of searchAreas(w)) {
    if (indexOfItem(grid, rootIid) >= 0) {
      current = grid;
      break;
    }
  }
  if (current === null) return null;

  let out: OwnedContents | null = null;
  for (let i = 0; i < chain.length; i++) {
    const iid = chain[i] as string;
    const already = w.owned.get(iid);
    if (already !== undefined) {
      out = already;
      current = already.grid;
      continue;
    }
    const index = indexOfItem(current, iid);
    if (index < 0) return null;
    const placed = current.items[index] as {
      item: ItemInstance;
      x: number;
      y: number;
      rot: Rotation;
    };
    const def = getItemDef(placed.item.defId);
    if (def === null || def.kind !== "container") return null;

    // `contentsGridFor` returns either the instance's own (shared) grid or a
    // fresh empty one; cloning covers both.
    const grid = cloneGrid(contentsGridFor(def, placed.item));
    const host: ItemInstance = { ...placed.item, contents: grid };
    current.items[index] = { item: host, x: placed.x, y: placed.y, rot: placed.rot };

    out = { grid, host, hostDef: def, depth: i + 1, chain: chain.slice(0, i + 1) };
    w.owned.set(iid, out);
    current = grid;
  }
  return out;
}

/**
 * Locates an item across every reachable grid, nested ones included (never
 * equipment slots). Top-level hits allocate nothing; a nested hit freshens
 * its host chain BEFORE returning, since every caller goes on to splice.
 */
function locate(
  w: Work,
  iid: string,
): { grid: GridState; index: number; item: ItemInstance } | null {
  for (const { grid } of searchAreas(w)) {
    for (let i = 0; i < grid.items.length; i++) {
      const p = grid.items[i] as { item: ItemInstance };
      if (p.item.iid === iid) return { grid, index: i, item: p.item };
    }
  }

  const hit = findNestedItem(searchRoots(w), iid);
  if (hit === null || hit.chain.length === 0) return null;
  const hostIid = hit.chain[hit.chain.length - 1] as string;
  const owned = resolveContents(w, hostIid);
  if (owned === null) return null;
  const index = indexOfItem(owned.grid, iid);
  if (index < 0) return null;
  const p = owned.grid.items[index] as { item: ItemInstance };
  return { grid: owned.grid, index, item: p.item };
}

/**
 * A resolved destination plus what the nesting rules need. `hostDef` is `null`,
 * `depth` 0 and `chain` empty for a top-level area.
 */
interface TargetGrid {
  grid: GridState;
  hostDef: ContainerItemDef | null;
  depth: number;
  chain: readonly string[];
}

/** Resolves a `TargetRef` to a writable grid, or to the reason it cannot be. */
function targetGridOf(w: Work, to: TargetRef): TargetGrid | InvOpReason {
  if (to.area === "inside") {
    const owned = resolveContents(w, to.iid);
    if (owned === null) return "no-area";
    // Unreachable through data any op in this file produced, but a stale or
    // hostile payload can name a host that is already too deep to open.
    if (owned.depth > MAX_GRID_DEPTH) return "too-deep";
    return { grid: owned.grid, hostDef: owned.hostDef, depth: owned.depth, chain: owned.chain };
  }
  const grid = gridOf(w, to.area);
  if (grid === null) return "no-area";
  return { grid, hostDef: null, depth: 0, chain: [] };
}

/**
 * The two nesting checks every placement passes before bounds/overlap.
 * `subtree` is `subtreeDepth` of the thing placed (0 for anything holding
 * nothing). Both are no-ops for a top-level destination.
 */
function placementGate(dst: TargetGrid, def: ItemDef, subtree: number): InvOpReason | null {
  if (dst.hostDef !== null && !containerAccepts(dst.hostDef, def)) return "filtered";
  if (dst.depth + subtree > MAX_GRID_DEPTH) return "too-deep";
  return null;
}

/**
 * Where a modifiable weapon lives: a weapon slot, or any reachable grid cell.
 */
type WeaponSite =
  | { where: "equipped"; slot: WeaponEquipSlot; item: ItemInstance }
  | { where: "grid"; grid: GridState; index: number; item: ItemInstance };

/**
 * Locates a weapon anywhere reachable. `locate` freshens a nested host chain,
 * so replacing the returned element stays pure.
 */
function findOwnedWeapon(w: Work, iid: string): WeaponSite | null {
  for (const slot of WEAPON_EQUIP_SLOTS) {
    const equipped = w.inv.equipment[slot];
    if (equipped !== null && equipped.iid === iid) {
      return { where: "equipped", slot, item: equipped };
    }
  }

  const found = locate(w, iid);
  return found === null ? null : { where: "grid", ...found };
}

/**
 * The gun whose `mag` slot holds `magIid`, or `null` — the redirect that lets
 * `load`/`unload` work on the workbench, where a fitted magazine is drawn as
 * its own object but lives in a `mods` map `locate` cannot see. Same search
 * order as `findOwnedWeapon`.
 */
function findWeaponWithFittedMag(w: Work, magIid: string): WeaponSite | null {
  const holds = (item: ItemInstance): boolean => item.mods?.["mag"]?.iid === magIid;

  for (const slot of WEAPON_EQUIP_SLOTS) {
    const equipped = w.inv.equipment[slot];
    if (equipped !== null && holds(equipped)) {
      return { where: "equipped", slot, item: equipped };
    }
  }

  // `locate`'s order, spelled out because the key is a MOD's iid.
  for (const { grid } of searchAreas(w)) {
    for (let i = 0; i < grid.items.length; i++) {
      const p = grid.items[i] as { item: ItemInstance };
      if (holds(p.item)) return { where: "grid", grid, index: i, item: p.item };
    }
  }
  for (const root of searchRoots(w)) {
    const hit = findHostingWeaponIn(root, magIid, 0);
    if (hit === null) continue;
    // Re-`locate` it so the whole host chain is freshened and the grid the
    // caller writes into is one this Work owns.
    const found = locate(w, hit);
    if (found === null) return null;
    return { where: "grid", ...found };
  }
  return null;
}

/** The iid of the weapon inside `grid` (recursively) whose `mag` slot holds
 *  `magIid`. Read-only and depth-capped, exactly like `findNestedItem`. */
function findHostingWeaponIn(grid: GridState, magIid: string, depth: number): string | null {
  for (const p of grid.items) {
    if (p.item.mods?.["mag"]?.iid === magIid) return p.item.iid;
  }
  if (depth >= MAX_GRID_DEPTH) return null;
  for (const p of grid.items) {
    const contents = p.item.contents;
    if (contents === undefined) continue;
    const hit = findHostingWeaponIn(contents, magIid, depth + 1);
    if (hit !== null) return hit;
  }
  return null;
}

/** Writes a replacement weapon instance back wherever the original was. */
function replaceWeapon(w: Work, site: WeaponSite, next: ItemInstance): void {
  if (site.where === "equipped") {
    w.inv.equipment[site.slot] = next;
    return;
  }
  const placed = site.grid.items[site.index] as { x: number; y: number; rot: Rotation };
  site.grid.items[site.index] = { item: next, x: placed.x, y: placed.y, rot: placed.rot };
}

/**
 * The weapon def behind an instance: the def, `"no-def"` (unknown id), or
 * `null` (known but not a weapon → "no-slot").
 */
function weaponDefOf(item: ItemInstance): WeaponItemDef | "no-def" | null {
  const def = getItemDef(item.defId);
  if (def === null) return "no-def";
  return def.kind === "weapon" ? def : null;
}

/** The declared slot of that kind on one HOST — a gun or a part of one
 *  (protocol v18) — or `null` if it declares no such mounting point. */
function slotOn(
  def: WeaponItemDef | AttachmentItemDef,
  slot: AttachKind,
): WeaponSlotDef | null {
  for (const s of def.slots ?? []) {
    if (s.id === slot) return s;
  }
  return null;
}

/**
 * How many levels of parts hang off `item` (0 bare, 1, 2) — `subtreeDepth`'s
 * twin for `MAX_MOD_DEPTH`. Counts only parts `fittedMods` believes.
 */
function modSubtreeDepth(def: AttachmentItemDef, item: ItemInstance, depth = 0): number {
  if (depth >= MAX_MOD_DEPTH) return 0;
  let deepest = 0;
  for (const mod of fittedMods(def, item)) {
    const below = 1 + modSubtreeDepth(mod.def, mod.item, depth + 1);
    if (below > deepest) deepest = below;
  }
  return deepest;
}

/**
 * Restores "empty means absent" on every host this op freshened. Editing them
 * in place is safe — `resolveContents` minted them. A nested host can never be
 * the empty one, so one flat pass suffices.
 */
function normalize(w: Work): void {
  for (const owned of w.owned.values()) {
    if (owned.grid.items.length === 0) delete owned.host.contents;
  }
}

function reject(reason: InvOpReason): InventoryOpResult {
  return { ok: false, reason };
}

function accept(w: Work, dropped?: ItemInstance): InventoryOpResult {
  normalize(w);
  const ctx: InvContext = {
    inv: w.inv,
    container:
      w.container === null
        ? null
        : w.body === null
          ? w.container
          : { ...w.container, equipment: equipmentFromBodySlots(w.body) },
    ...(w.containerOwned ? { containerOwned: true } : {}),
  };
  return dropped === undefined ? { ok: true, ctx } : { ok: true, ctx, dropped };
}

// ---------------------------------------------------------------------------

/**
 * Applies one operation. `nextIid` is called at most once for `split`, once
 * per new stack for `unload`, never otherwise — and never on a rejection path.
 * Client ids are provisional; the server's `inv` replaces them. Never throws
 * for an op that typechecks.
 */
export function applyInventoryOp(
  ctx: InvContext,
  op: InventoryOp,
  nextIid: () => string,
): InventoryOpResult {
  const w = cloneCtx(ctx);

  switch (op.t) {
    // -----------------------------------------------------------------------
    case "move": {
      const src = locate(w, op.iid);
      if (src === null) return reject("no-item");
      const def = getItemDef(src.item.defId);
      if (def === null) return reject("no-def");
      const dst = targetGridOf(w, op.to);
      if (typeof dst === "string") return reject(dst);

      // A bag may not swallow itself or anything it is inside; one membership
      // test on the chain covers both.
      if (dst.chain.includes(op.iid)) return reject("cycle");
      const gate = placementGate(dst, def, subtreeDepth(src.item));
      if (gate !== null) return reject(gate);

      // The moving item is excluded from its own overlap test. Same source and
      // target nested grid resolve to the same memoized object.
      const problem = placementProblem(dst.grid, def, op.to.x, op.to.y, op.to.rot, op.iid);
      if (problem !== null) return reject(problem);

      src.grid.items.splice(src.index, 1);
      dst.grid.items.push({ item: src.item, x: op.to.x, y: op.to.y, rot: op.to.rot });
      return accept(w);
    }

    // -----------------------------------------------------------------------
    case "equip": {
      const src = locate(w, op.iid);
      if (src === null) return reject("no-item");
      const def = getItemDef(src.item.defId);
      if (def === null) return reject("no-def");
      // A storage box (`equipSlotOf` null) never gets past this.
      if (!canEquipInSlot(def, op.slot)) return reject("wrong-slot");
      if (w.inv.equipment[op.slot] !== null) return reject("slot-occupied");

      // The capacity grid of an empty slot must itself be absent.
      if (op.slot === "rig" || op.slot === "backpack") {
        const stale = w.inv[op.slot];
        if (stale !== null && stale.items.length > 0) return reject("not-empty");
      }

      src.grid.items.splice(src.index, 1);

      // HOIST: the bag's contents become the slot's grid and the worn instance
      // drops `contents`. Keyed off `op.slot`, which narrows the index.
      if ((op.slot === "rig" || op.slot === "backpack") && def.kind === "container") {
        const contents = src.item.contents;
        w.inv[op.slot] =
          contents === undefined ? createContainerGrid(def) : cloneGrid(contents);
        const worn: ItemInstance = { ...src.item };
        delete worn.contents;
        w.inv.equipment[op.slot] = worn;
      } else {
        w.inv.equipment[op.slot] = src.item;
      }
      return accept(w);
    }

    // -----------------------------------------------------------------------
    case "unequip": {
      const item = w.inv.equipment[op.slot];
      if (item === null) return reject("slot-empty");
      const def = getItemDef(item.defId);
      if (def === null) return reject("no-def");

      // WRAP: a rig or backpack takes its contents with it. The grid is already
      // this Work's clone; an empty one is not adopted (absent = empty).
      let placedItem = item;
      if (op.slot === "rig" || op.slot === "backpack") {
        const capacity = w.inv[op.slot];
        if (capacity !== null && capacity.items.length > 0) {
          placedItem = { ...item, contents: capacity };
        }
      }

      // Clear FIRST, then resolve: unequipping a backpack into itself must fail
      // with "no-area" rather than dropping the bag into itself.
      w.inv.equipment[op.slot] = null;
      if (op.slot === "rig" || op.slot === "backpack") w.inv[op.slot] = null;

      const dst = targetGridOf(w, op.to);
      if (typeof dst === "string") return reject(dst);
      // A filtered box refuses a bag outright; depth is checked against the
      // WRAPPED item.
      const gate = placementGate(dst, def, subtreeDepth(placedItem));
      if (gate !== null) return reject(gate);
      const problem = placementProblem(dst.grid, def, op.to.x, op.to.y, op.to.rot);
      if (problem !== null) return reject(problem);

      dst.grid.items.push({ item: placedItem, x: op.to.x, y: op.to.y, rot: op.to.rot });
      return accept(w);
    }

    // -----------------------------------------------------------------------
    case "reslot": {
      // Shoulder ↔ holster is not unequip + equip: no grid cell in between, so
      // full pockets cannot refuse the swap.
      if (op.from === op.to) return reject("same-slot");
      const item = w.inv.equipment[op.from];
      if (item === null) return reject("slot-empty");
      const def = getItemDef(item.defId);
      if (def === null) return reject("no-def");
      if (!canEquipInSlot(def, op.to)) return reject("wrong-slot");

      // An occupied destination is a SWAP only if the gun there can live where
      // this one came from.
      const other = w.inv.equipment[op.to];
      if (other !== null) {
        const otherDef = getItemDef(other.defId);
        if (otherDef === null) return reject("no-def");
        if (!canEquipInSlot(otherDef, op.from)) return reject("slot-occupied");
      }

      // `other` is `null` for an empty destination, so swap and move are the
      // same two assignments.
      w.inv.equipment[op.to] = item;
      w.inv.equipment[op.from] = other;
      return accept(w);
    }

    // -----------------------------------------------------------------------
    case "split": {
      const src = locate(w, op.iid);
      if (src === null) return reject("no-item");
      const def = getItemDef(src.item.defId);
      if (def === null) return reject("no-def");
      if (!isStackable(def)) return reject("not-stackable");
      // Strictly inside (0, stack): the whole stack is a move, zero is a no-op.
      if (!Number.isSafeInteger(op.count) || op.count <= 0 || op.count >= src.item.stack) {
        return reject("bad-count");
      }
      const dst = targetGridOf(w, op.to);
      if (typeof dst === "string") return reject(dst);
      // The new pile is a bare stack — nothing stackable can hold anything —
      // so its subtree depth is a literal 0 and only the filter can bite.
      const gate = placementGate(dst, def, 0);
      if (gate !== null) return reject(gate);
      const problem = placementProblem(dst.grid, def, op.to.x, op.to.y, op.to.rot);
      if (problem !== null) return reject(problem);

      const placed = src.grid.items[src.index] as { x: number; y: number; rot: Rotation };
      src.grid.items[src.index] = {
        item: { ...src.item, stack: src.item.stack - op.count },
        x: placed.x,
        y: placed.y,
        rot: placed.rot,
      };
      // Only stackables get here, and stackables carry no per-unit state, so
      // the new instance needs nothing but an id, a def and a count.
      dst.grid.items.push({
        item: { iid: nextIid(), defId: src.item.defId, stack: op.count },
        x: op.to.x,
        y: op.to.y,
        rot: op.to.rot,
      });
      return accept(w);
    }

    // -----------------------------------------------------------------------
    case "merge": {
      if (op.iid === op.ontoIid) return reject("same-item");
      const src = locate(w, op.iid);
      if (src === null) return reject("no-item");
      const dst = locate(w, op.ontoIid);
      if (dst === null) return reject("no-item");
      if (src.item.defId !== dst.item.defId) return reject("def-mismatch");
      const def = getItemDef(dst.item.defId);
      if (def === null) return reject("no-def");
      if (!isStackable(def)) return reject("not-stackable");

      const space = def.stackMax - dst.item.stack;
      if (space <= 0) return reject("full");
      // Partial merges are the norm: top up to stackMax, leave the remainder.
      const moved = space < src.item.stack ? space : src.item.stack;

      // Nothing is placed, so no filter or depth gate: both stacks are already
      // legally where they are.
      const dstPlaced = dst.grid.items[dst.index] as { x: number; y: number; rot: Rotation };
      dst.grid.items[dst.index] = {
        item: { ...dst.item, stack: dst.item.stack + moved },
        x: dstPlaced.x,
        y: dstPlaced.y,
        rot: dstPlaced.rot,
      };

      const left = src.item.stack - moved;
      if (left === 0) {
        // `src.index` is still valid: every edit so far replaced an element in
        // place, host-chain freshening included.
        src.grid.items.splice(src.index, 1);
      } else {
        const srcPlaced = src.grid.items[src.index] as { x: number; y: number; rot: Rotation };
        src.grid.items[src.index] = {
          item: { ...src.item, stack: left },
          x: srcPlaced.x,
          y: srcPlaced.y,
          rot: srcPlaced.rot,
        };
      }
      return accept(w);
    }

    // -----------------------------------------------------------------------
    case "load": {
      const src = locate(w, op.iid);
      if (src === null) return reject("no-item");
      const ammoDef = getItemDef(src.item.defId);
      if (ammoDef === null) return reject("no-def");
      // Everything below assumes a cartridge: a caliber to check, a stack to
      // draw down, one round per unit.
      if (ammoDef.kind !== "ammo") return reject("not-ammo");

      /**
       * Draws `moved` units off the source stack. ALWAYS CALLED LAST: every
       * write above replaces in place, so `src.index` still holds.
       */
      const drawDown = (moved: number): void => {
        const left = src.item.stack - moved;
        if (left === 0) {
          src.grid.items.splice(src.index, 1);
          return;
        }
        const placed = src.grid.items[src.index] as { x: number; y: number; rot: Rotation };
        src.grid.items[src.index] = {
          item: { ...src.item, stack: left },
          x: placed.x,
          y: placed.y,
          rot: placed.rot,
        };
      };

      /**
       * The WEAPON path: rounds go into the fitted magazine. An empty well
       * refuses "slot-empty" — the shotgun's tube is a magazine too.
       */
      const fillWeapon = (site: WeaponSite, weaponDef: WeaponItemDef): InventoryOpResult => {
        if (weaponDef.caliber !== ammoDef.caliber) return reject("incompatible");
        // An empty well is a different failure from a full magazine, and the
        // player needs to be able to tell them apart.
        const fitted = fittedMagazine(weaponDef, site.item);
        if (fitted === null) return reject("slot-empty");

        const have = fitted.item.mag ?? 0;
        const space = fitted.def.magCapacity - have;
        if (space <= 0) return reject("full");
        if (have > 0 && fitted.item.ammoDefId !== ammoDef.id) return reject("def-mismatch");

        const moved = space < src.item.stack ? space : src.item.stack;
        replaceWeapon(w, site, weaponWithMagRounds(site.item, have + moved, ammoDef.id));
        drawDown(moved);
        return accept(w);
      };

      // (a) and (b): the named item itself, searched exactly where a weapon is
      // searched — the weapon slots, then every reachable grid at any depth.
      const named = findOwnedWeapon(w, op.ontoIid);
      if (named !== null) {
        const targetDef = getItemDef(named.item.defId);
        if (targetDef === null) return reject("no-def");
        if (targetDef.kind === "weapon") return fillWeapon(named, targetDef);

        // The MAGAZINE path: rounds onto the magazine's own instance. A
        // magazine
        // is never worn, so it is always a grid element.
        if (isTrueMagazine(targetDef) && named.where === "grid") {
          const mag: MagazineItemDef = targetDef;
          if (mag.caliber !== ammoDef.caliber) return reject("incompatible");
          const have = named.item.mag ?? 0;
          const space = mag.magCapacity - have;
          if (space <= 0) return reject("full");
          if (have > 0 && named.item.ammoDefId !== ammoDef.id) return reject("def-mismatch");

          const moved = space < src.item.stack ? space : src.item.stack;
          const placed = named.grid.items[named.index] as { x: number; y: number; rot: Rotation };
          named.grid.items[named.index] = {
            item: { ...named.item, mag: have + moved, ammoDefId: ammoDef.id },
            x: placed.x,
            y: placed.y,
            rot: placed.rot,
          };
          drawDown(moved);
          return accept(w);
        }

        // Located, and nothing pours into it. "no-slot" rather than "no-item":
        // the thing exists, it simply has no magazine well and is not one.
        return reject("no-slot");
      }

      // (c) `ontoIid` may name a magazine that is FITTED to a gun, which lives
      // in no grid at all. Filling it means filling its host.
      const host = findWeaponWithFittedMag(w, op.ontoIid);
      if (host === null) return reject("no-item");
      const hostDef = getItemDef(host.item.defId);
      if (hostDef === null) return reject("no-def");
      // Unreachable through honest data — only a weapon has a `mods` map the
      // search above looks at — but the narrowing has to be earned.
      if (hostDef.kind !== "weapon") return reject("no-slot");
      return fillWeapon(host, hostDef);
    }

    // -----------------------------------------------------------------------
    case "unload": {
      /**
       * Pours `rounds` of `ammoDef` into the player's grids; answers how many
       * landed. Two passes, and the order is the determinism story: top up
       * existing stacks (canonical grid order, array order; nothing placed, so
       * no gate), then mint new stacks in `stackMax` chunks at
       * `findFreePlacement` spots — one `nextIid` per stack, none before a
       * placement is certain. The owned open container goes last; a raid
       * crate never participates (as `detachTargetFor` does for parts).
       */
      const pourOut = (ammoDef: AmmoItemDef, rounds: number): number => {
        const grids: GridState[] = [];
        for (const area of ["pockets", "rig", "backpack"] as const) {
          const grid = gridOf(w, area);
          if (grid !== null) grids.push(grid);
        }
        // Only the private menu stash. `w.container` may be a corpse or a crate,
        // and neither is somewhere the player's rounds may end up.
        if (w.containerOwned && w.container !== null) grids.push(w.container.grid);

        let left = rounds;
        for (const grid of grids) {
          for (let i = 0; i < grid.items.length && left > 0; i++) {
            const placed = grid.items[i] as {
              item: ItemInstance;
              x: number;
              y: number;
              rot: Rotation;
            };
            if (placed.item.defId !== ammoDef.id) continue;
            const space = ammoDef.stackMax - placed.item.stack;
            if (space <= 0) continue;
            const moved = space < left ? space : left;
            grid.items[i] = {
              item: { ...placed.item, stack: placed.item.stack + moved },
              x: placed.x,
              y: placed.y,
              rot: placed.rot,
            };
            left -= moved;
          }
          if (left <= 0) break;
        }

        while (left > 0) {
          const chunk = left < ammoDef.stackMax ? left : ammoDef.stackMax;
          let placed = false;
          for (const grid of grids) {
            const at = findFreePlacement(grid, ammoDef);
            if (at === null) continue;
            grid.items.push({
              item: { iid: nextIid(), defId: ammoDef.id, stack: chunk },
              x: at.x,
              y: at.y,
              rot: at.rot,
            });
            placed = true;
            break;
          }
          // Nowhere left for a stack of any size: what is still in the magazine
          // stays there. A partial pour is the normal outcome of a full rig.
          if (!placed) break;
          left -= chunk;
        }
        return rounds - left;
      };

      /** The rounds in a magazine instance, resolved to a cartridge, or the
       *  reason there is nothing to pour. */
      const contentsOf = (
        item: ItemInstance,
      ): { ammoDef: AmmoItemDef; rounds: number } | InvOpReason => {
        const rounds = item.mag ?? 0;
        if (rounds <= 0) return "empty";
        const id = item.ammoDefId;
        if (id === undefined) return "no-def";
        const def = getItemDef(id);
        // An unknown cartridge id is version skew: "no-def".
        if (def === null || def.kind !== "ammo") return "no-def";
        return { ammoDef: def, rounds };
      };

      /** The WEAPON path: empty the magazine that is in the gun. */
      const emptyWeapon = (site: WeaponSite, weaponDef: WeaponItemDef): InventoryOpResult => {
        const fitted = fittedMagazine(weaponDef, site.item);
        if (fitted === null) return reject("slot-empty");
        const held = contentsOf(fitted.item);
        if (typeof held === "string") return reject(held);

        const poured = pourOut(held.ammoDef, held.rounds);
        if (poured === 0) return reject("full");
        replaceWeapon(w, site, weaponWithMagRounds(site.item, held.rounds - poured, null));
        return accept(w);
      };

      // (a) and (b): the named item itself, searched exactly where `load`
      // searches — the weapon slots, then every reachable grid at any depth.
      const named = findOwnedWeapon(w, op.iid);
      if (named !== null) {
        const targetDef = getItemDef(named.item.defId);
        if (targetDef === null) return reject("no-def");
        if (targetDef.kind === "weapon") return emptyWeapon(named, targetDef);

        // The LOOSE MAGAZINE path: always a grid element, and its rounds may
        // land in that same grid.
        if (isTrueMagazine(targetDef) && named.where === "grid") {
          const held = contentsOf(named.item);
          if (typeof held === "string") return reject(held);
          const poured = pourOut(held.ammoDef, held.rounds);
          if (poured === 0) return reject("full");

          // The pour only ever REPLACED elements or pushed new ones, so the
          // index this op has been holding since `locate` is still the magazine's.
          const left = held.rounds - poured;
          const emptied: ItemInstance = { ...named.item };
          if (left > 0) emptied.mag = left;
          else {
            delete emptied.mag;
            delete emptied.ammoDefId;
          }
          const placed = named.grid.items[named.index] as { x: number; y: number; rot: Rotation };
          named.grid.items[named.index] = {
            item: emptied,
            x: placed.x,
            y: placed.y,
            rot: placed.rot,
          };
          return accept(w);
        }

        // Located, and nothing pours out of it. "no-slot" rather than "no-item":
        // the thing exists, it simply holds no rounds and is not a magazine.
        return reject("no-slot");
      }

      // (c) `iid` may name a magazine that is FITTED to a gun, which lives in no
      // grid at all. Emptying it means emptying it through its host.
      const host = findWeaponWithFittedMag(w, op.iid);
      if (host === null) return reject("no-item");
      const hostDef = getItemDef(host.item.defId);
      if (hostDef === null) return reject("no-def");
      // Unreachable through honest data — only a weapon has a `mods` map the
      // search above looks at — but the narrowing has to be earned.
      if (hostDef.kind !== "weapon") return reject("no-slot");
      return emptyWeapon(host, hostDef);
    }

    // -----------------------------------------------------------------------
    case "drop": {
      const src = locate(w, op.iid);
      if (src !== null) {
        src.grid.items.splice(src.index, 1);
        return accept(w, src.item);
      }

      // Not in a grid — it may be worn. A rig or backpack is dropped FULL,
      // wrapped as `unequip` wraps it: one world item carrying a subtree.
      const eq = findEquipped(w.inv, op.iid);
      if (eq === null) return reject("no-item");
      let dropped = eq.item;
      if (eq.slot === "rig" || eq.slot === "backpack") {
        const capacity = w.inv[eq.slot];
        if (capacity !== null && capacity.items.length > 0) {
          dropped = { ...eq.item, contents: capacity };
        }
        w.inv[eq.slot] = null;
      }
      w.inv.equipment[eq.slot] = null;
      return accept(w, dropped);
    }

    // -----------------------------------------------------------------------
    case "attach": {
      const src = locate(w, op.iid);
      if (src === null) return reject("no-item");
      const attDef = getItemDef(src.item.defId);
      if (attDef === null) return reject("no-def");
      if (attDef.kind !== "attachment") return reject("not-attachment");

      // The PART may come from any depth, the GUN may not (`findOwnedWeapon`),
      // and it reads the state after `locate` may have freshened a chain.
      const site = findOwnedWeapon(w, op.weaponIid);
      if (site === null) return reject("no-item");
      const weaponDef = weaponDefOf(site.item);
      if (weaponDef === "no-def") return reject("no-def");
      // The answer when `weaponIid` names something that is not a weapon at
      // all: it has no mounting points, which is exactly "no-slot".
      if (weaponDef === null) return reject("no-slot");

      // Which host: absent `hostIid` is the gun. A named host must be a part
      // `fittedMods` believes.
      const host = findModHost(weaponDef, site.item, op.hostIid ?? site.item.iid);
      if (host === null) return reject("no-item");
      const slot = slotOn(host.def, op.slot);
      if (slot === null) return reject("no-slot");
      if (host.item.mods?.[op.slot] !== undefined) return reject("slot-occupied");
      if (!slot.accepts.includes(attDef.id)) return reject("incompatible");

      // A part goes on at `host.depth + 1` and carries its own subtree above
      // that. Content cannot author a tree past the cap, but a player can try
      // to
      // assemble one out of parts that individually fit.
      if (host.depth + 1 + modSubtreeDepth(attDef, src.item) > MAX_MOD_DEPTH) {
        return reject("too-deep");
      }

      // A magazine fits and keeps its rounds — nothing to move or refuse (one
      // kind of round is enforced in `load`). Replacement computed from the
      // ORIGINAL instance, part removed, weapon written back via a fresh locate
      // (the splice may have shifted the weapon's own grid).
      const next = weaponWithModAt(weaponDef, site.item, host.item.iid, op.slot, src.item);
      // Unreachable: `findModHost` just resolved that id on this very instance,
      // and `weaponWithModAt` walks the same tree with the same gates.
      if (next === null) return reject("no-item");
      src.grid.items.splice(src.index, 1);
      const fresh = site.where === "grid" ? findOwnedWeapon(w, op.weaponIid) : site;
      // Unreachable (only an attachment was removed); checked rather than
      // asserted.
      if (fresh === null) return reject("no-item");
      replaceWeapon(w, fresh, next);
      return accept(w);
    }

    // -----------------------------------------------------------------------
    case "detach": {
      const site = findOwnedWeapon(w, op.weaponIid);
      if (site === null) return reject("no-item");
      const weaponDef = weaponDefOf(site.item);
      if (weaponDef === "no-def") return reject("no-def");
      if (weaponDef === null) return reject("no-slot");

      // Same host resolution as `attach`, and absent means the gun for the same
      // reason: taking the scope off a rifle is still one id and a slot.
      const host = findModHost(weaponDef, site.item, op.hostIid ?? site.item.iid);
      if (host === null) return reject("no-item");
      const slot = slotOn(host.def, op.slot);
      if (slot === null) return reject("no-slot");
      const fitted = host.item.mods?.[op.slot];
      if (fitted === undefined) return reject("slot-empty");
      const attDef = getItemDef(fitted.defId);
      if (attDef === null) return reject("no-def");

      const dst = targetGridOf(w, op.to);
      if (typeof dst === "string") return reject(dst);
      // An attachment holds no grid, so its grid depth is 0 whatever comes off;
      // only the host's filter can refuse it.
      const gate = placementGate(dst, attDef, 0);
      if (gate !== null) return reject(gate);
      // Not in any grid yet, so nothing to exclude from the overlap test. Its
      // footprint is its own def's.
      const problem = placementProblem(dst.grid, attDef, op.to.x, op.to.y, op.to.rot);
      if (problem !== null) return reject(problem);

      // A magazine comes off carrying its rounds and a handguard its parts —
      // the instance in the slot goes into the grid unchanged.
      const next = weaponWithModAt(weaponDef, site.item, host.item.iid, op.slot, null);
      // Unreachable, for the reason it is in `attach`.
      if (next === null) return reject("no-item");
      replaceWeapon(w, site, next);
      dst.grid.items.push({ item: fitted, x: op.to.x, y: op.to.y, rot: op.to.rot });
      return accept(w);
    }
  }
}
